import { apiRequest } from '@/lib/api-client';
import { getStoredAuthToken } from '@/lib/auth-storage';

export type IndicadorVigenteApi = {
  indicador_id: number;
  codigo_interno: string;
  indicador: string;
  categoria: string;
  categoria_tematica?: string | null;
  tipo_indicador?: string | null;
  indicador_version_id: number;
  version: string;
  formula_tipo?: string | null;
  constante?: string | number | null;
  formula_texto?: string | null;
  formula_expression?: string | null;
  unidad_medida: string;
  unidad_simbolo?: string | null;
  periodicidad: string;
  publicable: boolean;
  sensible: boolean;
  vigente_desde?: string | null;
  vigente_hasta?: string | null;
};

export type ResultadoPublicoApi = {
  corrida_id: number;
  jurisdiccion: string;
  periodo_referencia: string;
  codigo_interno: string;
  indicador: string;
  categoria: string;
  unidad_medida: string;
  unidad_simbolo?: string | null;
  valor_resultado: string | number;
  calculado_at?: string;
  publicada_at?: string;
};

export type CorridaPublicadaApi = {
  id: number;
  titulo: string;
  jurisdiccion_id?: number;
  jurisdiccion?: {
    id: number;
    nombre: string;
  } | string | null;
  periodo_referencia?: string | null;
  ejecutada_at?: string | null;
  aprobada_at?: string | null;
  publicada_at?: string | null;
  resultados_publicables_count?: number;
};

const normalizeList = <T,>(payload: unknown): T[] => {
  if (Array.isArray(payload)) {
    return payload as T[];
  }

  if (payload && typeof payload === 'object') {
    const data = (payload as { data?: unknown[] }).data;
    if (Array.isArray(data)) {
      return data as T[];
    }
  }

  return [];
};

const buildQueryString = (filters?: Record<string, string | number | boolean | undefined>): string => {
  if (!filters) return '';
  const parts = Object.entries(filters)
    .filter(([_, value]) => value !== undefined && value !== '')
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  return parts.length > 0 ? `?${parts.join('&')}` : '';
};

export const fetchIndicadoresVigentes = async (
  filters?: {
    codigo_interno?: string;
    categoria?: string;
    categoria_tematica?: string;
    tipo_indicador?: string;
    periodicidad?: string;
    publicable?: boolean;
    sensible?: boolean;
    per_page?: number;
  }
): Promise<IndicadorVigenteApi[]> => {
  const token = getStoredAuthToken();
  const queryString = buildQueryString(filters);
  const payload = await apiRequest<unknown>(`/api/externo/indicadores-vigentes${queryString}`, { token });
  return normalizeList<IndicadorVigenteApi>(payload);
};

export const fetchResultadosPublicos = async (
  filters?: {
    jurisdiccion?: string;
    codigo_interno?: string;
    indicador?: string;
    categoria?: string;
    periodo_desde?: string;
    periodo_hasta?: string;
    publicada_desde?: string;
    publicada_hasta?: string;
    per_page?: number;
  }
): Promise<ResultadoPublicoApi[]> => {
  const token = getStoredAuthToken();
  const queryString = buildQueryString(filters);
  const payload = await apiRequest<unknown>(`/api/externo/resultados-publicos${queryString}`, { token });
  return normalizeList<ResultadoPublicoApi>(payload);
};

export const fetchCorridasPublicadas = async (
  filters?: {
    jurisdiccion_id?: number;
    titulo?: string;
    periodo_desde?: string;
    periodo_hasta?: string;
    publicada_desde?: string;
    publicada_hasta?: string;
    per_page?: number;
  }
): Promise<CorridaPublicadaApi[]> => {
  const token = getStoredAuthToken();
  const queryString = buildQueryString(filters);
  const payload = await apiRequest<unknown>(`/api/externo/corridas-publicadas${queryString}`, { token });
  return normalizeList<CorridaPublicadaApi>(payload);
};

export const fetchIndicadorById = async (id: number | string): Promise<any> => {
  const token = getStoredAuthToken();
  return apiRequest<any>(`/api/indicadores/${id}`, { token });
};

export const createIndicador = async (data: any): Promise<any> => {
  const token = getStoredAuthToken();
  return apiRequest<any>('/api/indicadores', {
    token,
    method: 'POST',
    body: data,
  });
};

export const updateIndicador = async (id: number | string, data: any): Promise<any> => {
  const token = getStoredAuthToken();
  return apiRequest<any>(`/api/indicadores/${id}`, {
    token,
    method: 'PUT',
    body: data,
  });
};
